import type { Task } from '@/types';
import { dayjs } from '@/utilities';

export const ACTIVE_TASK_BUCKET_KEYS = ['noDueDate', 'overdue', 'today', 'upcoming'] as const;
export const TASK_BUCKET_KEYS = [...ACTIVE_TASK_BUCKET_KEYS, 'completed'] as const;

export type ActiveTaskBucketKey = (typeof ACTIVE_TASK_BUCKET_KEYS)[number];
export type TaskBucketKey = (typeof TASK_BUCKET_KEYS)[number];
export type TaskBucketsFor<TTask> = Record<TaskBucketKey, TTask[]>;
export type CategorizedTaskBuckets = TaskBucketsFor<Task>;

export const createEmptyTaskBuckets = <TTask = Task>(): TaskBucketsFor<TTask> => ({
    completed: [],
    noDueDate: [],
    overdue: [],
    today: [],
    upcoming: [],
});

export const normalizeTaskBuckets = (buckets: Record<string, Task[]>): CategorizedTaskBuckets =>
    TASK_BUCKET_KEYS.reduce<CategorizedTaskBuckets>((normalized, key) => {
        normalized[key] = Array.isArray(buckets[key]) ? buckets[key] : [];
        return normalized;
    }, createEmptyTaskBuckets());

export const taskBucketFor = (task: Pick<Task, 'completed' | 'dueAt'>): TaskBucketKey => {
    if (task.completed) {
        return 'completed';
    }
    if (!task.dueAt) {
        return 'noDueDate';
    }

    const today = dayjs().startOf('day');
    const dueAt = dayjs(task.dueAt);

    if (dueAt.isBefore(today)) {
        return 'overdue';
    }
    if (dueAt.isSame(today, 'day')) {
        return 'today';
    }

    return 'upcoming';
};

export const removeTaskFromBuckets = (buckets: CategorizedTaskBuckets, taskId: string): CategorizedTaskBuckets =>
    TASK_BUCKET_KEYS.reduce<CategorizedTaskBuckets>((nextBuckets, key) => {
        nextBuckets[key] = buckets[key].filter((task) => task.id !== taskId);
        return nextBuckets;
    }, createEmptyTaskBuckets());

export const getTaskFromBuckets = (buckets: CategorizedTaskBuckets, taskId: string): Task | undefined =>
    TASK_BUCKET_KEYS.flatMap((key) => buckets[key]).find((task) => task.id === taskId);

export const upsertTaskInBuckets = (buckets: CategorizedTaskBuckets, task: Task): CategorizedTaskBuckets => {
    const nextBuckets = removeTaskFromBuckets(buckets, task.id);
    const key = taskBucketFor(task);

    nextBuckets[key] = [task, ...nextBuckets[key]].sort(
        (left, right) => dayjs(right.updatedAt).valueOf() - dayjs(left.updatedAt).valueOf(),
    );

    return nextBuckets;
};
